import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../store';
import { MobileSheetHandle, useMobileSheetExpand } from './MobileSheet';
import {
  SHORTCUT_ORDER,
  assignShortcut,
  formatShortcutKey,
  type ShortcutAction,
} from '../lib/keyboardShortcuts';

export function ShortcutsPanel() {
  const { t } = useTranslation(['panels', 'common']);
  const { setPanel, shortcuts, setShortcutKey, resetShortcuts } = useApp();
  const { className: sheetExpandClassName } = useMobileSheetExpand('sm');
  const [listening, setListening] = useState<ShortcutAction | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  useEffect(() => {
    if (!listening) return;
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === 'Escape') {
        setListening(null);
        setHint(null);
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) {
        setHint(t('shortcuts.needPlainKey'));
        return;
      }
      const next = assignShortcut(shortcuts, listening, e.key);
      if (next[listening] === shortcuts[listening] && normalizeFailed(e.key)) {
        setHint(t('shortcuts.unsupportedKey'));
        return;
      }
      setShortcutKey(listening, e.key);
      setListening(null);
      setHint(null);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [listening, shortcuts, setShortcutKey, t]);

  return (
    <aside className={`absolute inset-x-0 bottom-0 z-30 max-h-[85vh] overflow-auto rounded-t-3xl border border-[var(--line)] bg-[var(--bg-panel)] p-5 shadow-2xl backdrop-blur-xl sm:inset-x-auto sm:right-4 sm:top-24 sm:bottom-auto sm:w-[24rem] sm:rounded-3xl ${sheetExpandClassName}`}>
      <MobileSheetHandle />
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-[family-name:var(--font-display)] text-xl text-[var(--accent)]">{t('shortcuts.title')}</h2>
          <p className="text-xs text-[var(--muted)]">{t('shortcuts.hint')}</p>
        </div>
        <button type="button" className="shrink-0 text-[var(--muted)]" onClick={() => setPanel('profile')}>
          {t('common:back')}
        </button>
      </div>

      <ul className="flex flex-col gap-2">
        {SHORTCUT_ORDER.map((action) => {
          const active = listening === action;
          return (
            <li
              key={action}
              className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 ${
                active
                  ? 'border-[var(--accent)] bg-[var(--accent)]/10'
                  : 'border-[var(--line)] bg-[#0b1219]/40'
              }`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-[var(--ink)]">{t(`common:shortcuts.${action}`)}</p>
                {active && (
                  <p className="mt-0.5 text-[11px] text-[var(--accent)]">{t('shortcuts.listening')}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setHint(null);
                  setListening((cur) => (cur === action ? null : action));
                }}
                className={`shrink-0 rounded-lg border px-3 py-1.5 font-mono text-sm font-bold tabular-nums transition ${
                  active
                    ? 'border-[var(--accent)] text-[var(--accent)]'
                    : 'border-[var(--line)] text-[var(--ink)] hover:border-[var(--accent)] hover:text-[var(--accent)]'
                }`}
                title={t('shortcuts.modify')}
              >
                {formatShortcutKey(shortcuts[action])}
              </button>
            </li>
          );
        })}
      </ul>

      {hint && <p className="mt-3 text-xs text-[var(--danger)]">{hint}</p>}

      <button
        type="button"
        onClick={() => {
          resetShortcuts();
          setListening(null);
          setHint(null);
        }}
        className="mt-4 w-full rounded-xl border border-[var(--line)] px-4 py-2 text-sm text-[var(--muted)] hover:border-[var(--accent)] hover:text-[var(--ink)]"
      >
        {t('shortcuts.reset')}
      </button>
    </aside>
  );
}

function normalizeFailed(raw: string): boolean {
  if (raw.length !== 1) return true;
  return !/^[a-z0-9]$/i.test(raw);
}
